/**
 * Data layer for `top-publishers-widget`. Thin typed fetch wrappers over /api/v1/top-publishers-widget.
 * Raw `fetch()` is confined to this file per the dash D6 contract.
 */

export interface PublisherRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	affiliate?: string;
	email?: string;
	clicks?: number;
	conversions?: number;
	cr?: number;
	payout?: number;
	sparkline?: number[];
	[key: string]: unknown;
}

// Back-compat alias — some downstream imports may expect TopPublishersWidgetRow.
export type TopPublishersWidgetRow = PublisherRow;

export interface ListTopPublishersWidgetResponse {
	count: number;
	items: PublisherRow[];
}

export interface ListTopPublishersWidgetParams {
	period?: "7d" | "30d" | "90d" | "ytd";
	limit?: number;
}

export interface CreateTopPublishersWidgetBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/top-publishers-widget";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

function buildQuery(params?: ListTopPublishersWidgetParams): string {
	if (!params) return "";
	const usp = new URLSearchParams();
	if (params.period) usp.set("period", params.period);
	if (typeof params.limit === "number") usp.set("limit", String(params.limit));
	const q = usp.toString();
	return q ? `?${q}` : "";
}

export async function list(
	params?: ListTopPublishersWidgetParams,
): Promise<ListTopPublishersWidgetResponse> {
	const res = await fetch(`${BASE}${buildQuery(params)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListTopPublishersWidgetResponse>(res);
}

export async function create(body: CreateTopPublishersWidgetBody): Promise<PublisherRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<PublisherRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const topPublishersWidgetQueryKeys = {
	all: ["top-publishers-widget"] as const,
	list: (params?: ListTopPublishersWidgetParams) =>
		[...topPublishersWidgetQueryKeys.all, params ?? {}] as const,
	detail: (id: string) => [...topPublishersWidgetQueryKeys.all, id] as const,
};
